import {
  ArrowSquareOut,
  Copy,
  PencilSimpleLine,
  Plus,
  ShareNetwork,
  Trash,
} from '@phosphor-icons/react'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import RichTextEditor from '@/components/shared/components/editor/RichTextEditor'
import ImageDropzone from '@/components/shared/components/ImageDropzone'
import AlertDialog from '@/components/shared/ui/AlertDialog'
import Button from '@/components/shared/ui/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '@/components/shared/ui/Dialog/Dialog'
import Dropdown from '@/components/shared/ui/Dropdown'
import {
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from '@/components/shared/ui/Dropdown/Dropdown'
import Input from '@/components/shared/ui/Input'
import Label from '@/components/shared/ui/Label'
import Spinner from '@/components/shared/ui/Loader'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { ENTITIES, EVENT_ACTION_TYPES, EventEmitter } from '@/lib/eventEmitter'
import { copyClipboard, stateKeyValueChange } from '@/lib/helpers/appHelpers'
import { jsonToFormData } from '@/lib/helpers/dataHelpers'
import { formatJSONTimestamp } from '@/lib/helpers/dateHelpers'
import {
  addKanbanEntry,
  deleteKanbanEntry,
  getAdminKanbanEntry,
  getKanbanEntry,
  updateKanbanEntry,
} from '@/models/Kanban'
import type { IEditorRef } from '@/types/editor'
import type {
  IKanbanColumn,
  IKanbanColumnCard,
  IKanbanColumnCreateData,
  IKanbanItem,
} from '@/types/roadmap'
import toaster from '@/utils/toast'

import KanbanEntryItemPreview from './KanbanEntryItemPreview'
import KanbanNewEntryPostPicker from './KanbanNewEntryPostPicker'

interface IPropTypes {
  lane?: IKanbanColumn
  onAdd?: (laneId: string, item: IKanbanColumnCard) => void
  onUpdate?: (item: IKanbanColumnCard) => void
  kanbanItem?: IKanbanItem
  children?: React.ReactNode
  readonly?: boolean
  open?: boolean
  onClose?: () => void
  noTrigger?: boolean
}
export default function KanbanNewEntryDialog({
  lane,
  onAdd,
  onUpdate,
  kanbanItem: _kanbanItem,
  children,
  readonly,
  open: _open,
  onClose,
  noTrigger = false,
}: IPropTypes) {
  const t = useTranslations('roadmap.kanban.addNewEntry')
  const router = useRouter()
  const { roadmapId } = router.query as { roadmapId: string }
  const { organization } = useContext(HNContext)
  const [kanbanItem, setKanbanItem] = useState(_kanbanItem)
  const [edit, setEdit] = useState(!kanbanItem?.id && !noTrigger)
  const [loading, setLoading] = useState(!!kanbanItem?.id || !!roadmapId)
  const [updating, setUpdating] = useState(false)

  const [open, setOpen] = useState(_open)

  const [formData, setFormData] = useState<IKanbanColumnCreateData>(
    kanbanItem || {
      feature_request_ids: [],
      posts: [],
    }
  )

  const descriptionEl = useRef<IEditorRef>(null)

  const cleanupFormData = (_formData: IKanbanColumnCreateData) => {
    const { title, posts, two_liner, description, header_image } = _formData
    return {
      title,
      feature_request_ids: posts?.map((p) => p.id),
      two_liner: two_liner || null,
      description: description || null,
      header_image: header_image || null,
    }
  }

  const handleClose = () => {
    setOpen(false)
    if (onClose) onClose()
  }

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault()
    if (descriptionEl.current && !descriptionEl.current?.isReadyToSubmit()) {
      return toaster.info({
        message: t('messages.imagesUploading', { ignorePrefix: true }),
      })
    }
    setUpdating(true)
    const kanbanFormData = cleanupFormData(formData)
    if (lane) {
      return addKanbanEntry(lane.id, jsonToFormData(kanbanFormData))
        .then((data) => {
          if (onAdd) onAdd(lane.id, data)
          handleClose()
        })
        .catch(toaster.error)
        .finally(() => setUpdating(false))
    }
    if (kanbanItem?.id) {
      return updateKanbanEntry(kanbanItem.id, jsonToFormData(kanbanFormData))
        .then((data) => {
          setKanbanItem(data)
          if (onUpdate) onUpdate(data)

          EventEmitter.dispatch('ENTITY_UPDATE', {
            entity: ENTITIES.KANBAN_ITEM,
            actionType: EVENT_ACTION_TYPES.UPDATE,
            data: {
              data: {
                ...kanbanItem,
                ...data,
                laneId: kanbanItem.kanban_id?.toString(),
              },
              id: data.id,
            },
          })
          setEdit(false)
        })
        .catch(toaster.error)
        .finally(() => setUpdating(false))
    }
    return null
  }

  const handleFetchData = () => {
    if (!kanbanItem?.id && !roadmapId) return
    const fetchFnc = readonly ? getKanbanEntry : getAdminKanbanEntry
    setLoading(true)
    fetchFnc(roadmapId || kanbanItem?.id || '')
      .then((item) => {
        setKanbanItem(item)
        setFormData(item)
      })
      .catch(toaster.error)
      .finally(() => setLoading(false))
  }

  const handleDelete = () => {
    if (!kanbanItem?.id) return
    deleteKanbanEntry(kanbanItem?.id)
      .then(() => setOpen(false))
      .then(() =>
        EventEmitter.dispatch('ENTITY_UPDATE', {
          entity: ENTITIES.KANBAN_ITEM,
          actionType: EVENT_ACTION_TYPES.DELETE,
          data: {
            id: kanbanItem?.id,
            data: kanbanItem,
          },
        })
      )
      .catch(toaster.error)
  }

  useEffect(() => {
    if (open && (kanbanItem?.id || !!roadmapId)) {
      handleFetchData()
    }
  }, [open])

  useEffect(() => {
    setOpen(_open)
  }, [_open])

  const renderForm = () => (
    <div className='space-y-2 lg:col-span-6'>
      <Input
        type='text'
        defaultValue={formData.title}
        label={t('fields.title.label')}
        placeholder={t('fields.title.placeholder')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFormData({ ...formData, title: e.target.value })
        }
      />
      <Input
        type='text'
        name='two_liner'
        defaultValue={formData.two_liner}
        label={t('fields.twoLiner.label')}
        placeholder={t('fields.twoLiner.placeholder')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFormData({ ...formData, two_liner: e.target.value })
        }
      />
      <div className='space-y-1'>
        <Label>{t('fields.coverImage.label')}</Label>
        <ImageDropzone
          message={t('fields.coverImage.description')}
          image={formData?.header_image}
          onDrop={(files) =>
            new Promise((resolve) => {
              stateKeyValueChange(
                'header_image',
                files && files.length ? files[0] : null,
                setFormData
              )
              resolve(true)
            })
          }
        />
      </div>
      <div className='space-y-1'>
        <Label>{t('fields.description.label')}</Label>
        <RichTextEditor
          ref={descriptionEl}
          defaultValue={formData.description}
          placeholder={t('fields.description.placeholder')}
          onChange={(content) =>
            stateKeyValueChange('description', content, setFormData)
          }
        />
      </div>
    </div>
  )

  const renderContent = () => {
    if (!kanbanItem) return <></>
    if (loading) return <Spinner />
    return (
      <div className='lg:col-span-6'>
        <KanbanEntryItemPreview kanbanItem={kanbanItem} />
      </div>
    )
  }

  const title = useMemo(() => {
    if (loading) return ''
    return t(kanbanItem?.id ? 'editTitle' : 'title', {
      data: {
        title: kanbanItem?.created_at?.timestamp
          ? formatJSONTimestamp(kanbanItem?.created_at?.timestamp, 'MMM dd')
          : lane?.title,
      },
    })
  }, [kanbanItem?.id, loading])

  return (
    <>
      <Dialog
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => handleClose()}
      >
        {!noTrigger && (
          <DialogTrigger>
            {children || (
              <Button
                size='xs'
                as='span'
                fab
                variant='secondary'
                icon={
                  <Plus weight='bold' className='flex h-3.5 w-3.5 shrink-0' />
                }
              />
            )}
          </DialogTrigger>
        )}

        <DialogContent backdrop size='full'>
          <div className='flex justify-between gap-2'>
            <div>
              <p>{title}</p>
            </div>
            <div className='mr-6 flex items-center gap-2'>
              {edit && (
                <>
                  <AlertDialog
                    title={t('actions.cancel.alertTexts.title')}
                    description={t('actions.cancel.alertTexts.description')}
                    onConfirm={() => {
                      if (kanbanItem?.id) {
                        setEdit(false)
                      } else {
                        setOpen(false)
                      }
                    }}
                    confirmText={t('buttons.discard', { ignorePrefix: true })}
                    type='danger'
                  >
                    <Button size='sm' variant='secondary' type='button'>
                      {t('buttons.cancel', { ignorePrefix: true })}
                    </Button>
                  </AlertDialog>
                  <Button
                    type='submit'
                    size='sm'
                    onClick={() => handleSubmit()}
                    disabled={updating}
                    loading={updating}
                  >
                    {t(kanbanItem?.id ? 'buttons.update' : 'buttons.publish', {
                      ignorePrefix: true,
                    })}
                  </Button>
                </>
              )}
              {!edit && (
                <>
                  <Dropdown>
                    <DropdownTrigger>
                      <Button
                        variant='secondary'
                        type='button'
                        fab
                        icon={<ShareNetwork />}
                      />
                    </DropdownTrigger>
                    <DropdownContent>
                      <DropdownItem
                        itemValue={t('actions.copyLink.title')}
                        icon={<Copy />}
                        onSelect={() => {
                          copyClipboard(
                            `${organization?.home_page}roadmap/${kanbanItem?.id}`,
                            t('actions.copyLink.successMessage')
                          )
                        }}
                      />
                      {!readonly && (
                        <DropdownItem
                          itemValue={t('actions.viewCard.title')}
                          href={`${organization?.home_page}roadmap/${kanbanItem?.id}`}
                          target='_blank'
                          icon={<ArrowSquareOut />}
                        />
                      )}
                    </DropdownContent>
                  </Dropdown>
                  {!readonly && (
                    <>
                      <Button
                        variant='secondary'
                        fab
                        type='button'
                        icon={<PencilSimpleLine />}
                        onClick={() => setEdit(!edit)}
                      />
                      <AlertDialog
                        title={t('actions.delete.alertTexts.title')}
                        description={t('actions.delete.alertTexts.description')}
                        onConfirm={() => handleDelete()}
                        type='danger'
                      >
                        <Button
                          variant='secondary'
                          fab
                          type='button'
                          icon={<Trash />}
                        />
                      </AlertDialog>
                    </>
                  )}
                </>
              )}
              <DialogClose />
            </div>
          </div>
          {loading && <Spinner />}
          {!loading && (
            <form className='' onSubmit={handleSubmit}>
              <div className='my-4 grid gap-6 space-x-3 lg:grid-cols-9'>
                {edit && renderForm()}
                {!edit && renderContent()}
                <KanbanNewEntryPostPicker
                  readonly={readonly}
                  kanbanItem={{
                    ...(kanbanItem as IKanbanColumnCard),
                    ...formData,
                  }}
                  onAdd={(post) => {
                    stateKeyValueChange(
                      'posts',
                      [...(formData.posts || []), post],
                      setFormData
                    )
                  }}
                  onUpdate={setFormData}
                  onRemove={(post) => {
                    stateKeyValueChange(
                      'posts',
                      (formData.posts || []).filter((p) => p.id !== post.id),
                      setFormData
                    )
                  }}
                />
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
